import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { CulturalActivityStatus } from "@common/ADAPT.Common.Model/organisation/cultural-activity-status";
import { SearchParamValue } from "@common/route/route.service";
import { UrlFilterService } from "@common/shell/filter/url-filter.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { DurationSelector, IDuration } from "@common/ux/duration-selector";
import { ActivitiesService } from "@org-common/lib/activities/activities.service";
import { ActivityFilterParamKeys } from "@org-common/lib/activities/activities-metadata";
import { ActivityPageService } from "@org-common/lib/activities/activity-page.service";
import { CulturalActivityTypeMetadata, ICulturalActivityTypeMetadata } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import DevExpress from "devextreme";
import dxTagBox, { ContentReadyEvent } from "devextreme/ui/tag_box";
import isEqual from "lodash.isequal";

export interface IActivityFilterParams {
    dueWithin: IDuration;
    visibleTypes: ICulturalActivityTypeMetadata[];
    visibleStatuses: CulturalActivityStatus[];
}

type IContentReadyEventWithList = ContentReadyEvent & {
    component: DevExpress.events.EventInfo<dxTagBox> & { _$list: JQuery<HTMLDivElement> };
}

@Component({
    selector: "adapt-activity-filter",
    templateUrl: "./activity-filter.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ActivityFilterComponent extends BaseComponent implements OnInit {
    public readonly ActivityFilterParamKeys = Object.values(ActivityFilterParamKeys);
    public readonly ActivityStatuses = Object.entries(CulturalActivityStatus).map(([text, value]) => ({ text, value }));
    public readonly DurationOptions = DurationSelector.DataSource;

    @Input() public isCalendarView = false;
    @Output() public filterChange = new EventEmitter<IActivityFilterParams>();
    private lastEmittedFilter?: IActivityFilterParams;

    public CulturalActivityTypeMetadata = CulturalActivityTypeMetadata;
    public currentOptions!: IActivityFilterParams;
    private defaultOptions!: IActivityFilterParams;

    public constructor(
        private activityPageService: ActivityPageService,
        private activitiesService: ActivitiesService,
        private urlFilterService: UrlFilterService,
    ) {
        super();
    }

    public async ngOnInit() {
        const types = await this.activitiesService.getAccessibleActivityTypes();
        this.CulturalActivityTypeMetadata = CulturalActivityTypeMetadata.filter(({ type }) => types.includes(type));

        this.defaultOptions = {
            dueWithin: this.DurationOptions[2], // 1 month
            visibleTypes: [...this.CulturalActivityTypeMetadata],
            visibleStatuses: Object.values(CulturalActivityStatus),
        };

        this.currentOptions = this.activityPageService.filter ?? {
            ...this.defaultOptions,
            // need to make new arrays otherwise the default will be modified
            visibleTypes: [...this.defaultOptions.visibleTypes],
            visibleStatuses: [...this.defaultOptions.visibleStatuses],
        };

        this.setCurrentOptionsFromUrl();

        this.isInitialised = true;
        await this.emitOptionsChanged();
    }

    public get isDefault() {
        return isEqual(this.defaultOptions, this.currentOptions);
    }

    public async onDueWithinDateChanged(value: string) {
        this.currentOptions.dueWithin = this.getDurationFromSlug(value);
        await this.emitOptionsChanged();
    }

    public async onActivityTypesChanged(selectedTypes: ICulturalActivityTypeMetadata[]) {
        this.currentOptions.visibleTypes = selectedTypes;
        await this.emitOptionsChanged();
    }

    public async onStatusTypesChanged(selectedStatuses: CulturalActivityStatus[]) {
        this.currentOptions.visibleStatuses = selectedStatuses;
        await this.emitOptionsChanged();
    }

    public async resetOptions() {
        this.currentOptions = { ...this.defaultOptions };
        await this.emitOptionsChanged();
    }

    public async emitOptionsChanged() {
        // only emit if the options have actually changed
        if (this.isInitialised && !isEqual(this.currentOptions, this.lastEmittedFilter)) {
            await this.setUrlParams();
            this.filterChange.emit(this.currentOptions);
            this.lastEmittedFilter = { ...this.currentOptions };
        }
    }

    // add colours to checkboxes
    public onActivityTypeSelectorReady(event: ContentReadyEvent) {
        if (this.hasListProperty(event)) {
            const listElement = event.component._$list.get(0);
            const checkboxes = listElement?.querySelectorAll<HTMLSpanElement>(".dx-list-select-checkbox .dx-checkbox-icon");
            checkboxes?.forEach((checkbox, idx) => {
                checkbox.style.backgroundColor = CulturalActivityTypeMetadata[idx]?.colourAccent;
                checkbox.style.color = "#fff";
            });
        }
    }

    private hasListProperty(event: ContentReadyEvent): event is IContentReadyEventWithList {
        return "_$list" in event.component && !!event.component._$list;
    }

    private getDurationFromSlug(slug: string) {
        return this.DurationOptions.find((i) => i.slug === slug)
            ?? this.DurationOptions[0];
    }

    private async setUrlParams() {
        const fil: Record<ActivityFilterParamKeys, SearchParamValue> = {
            [ActivityFilterParamKeys.DueWithin]: !isEqual(this.defaultOptions.dueWithin, this.currentOptions.dueWithin)
                ? this.currentOptions.dueWithin.slug
                : undefined,
            [ActivityFilterParamKeys.HiddenTypes]: !isEqual(this.defaultOptions.visibleTypes, this.currentOptions.visibleTypes)
                ? this.culturalActivityTypesToUrl(this.CulturalActivityTypeMetadata.filter((i) => !this.currentOptions.visibleTypes.includes(i)))
                : undefined,
            [ActivityFilterParamKeys.HiddenStatuses]: !isEqual(this.defaultOptions.visibleStatuses, this.currentOptions.visibleStatuses)
                ? this.activityStatusesToUrl(Object.values(CulturalActivityStatus).filter((i) => !this.currentOptions.visibleStatuses.includes(i)))
                : undefined,
        };

        await this.urlFilterService.setFilter(fil);
    }

    private setCurrentOptionsFromUrl() {
        const paramValues = this.urlFilterService.getFilters(Object.values(ActivityFilterParamKeys));

        for (const param of paramValues) {
            if (param.value) {
                switch (param.name) {
                    case ActivityFilterParamKeys.DueWithin:
                        this.currentOptions.dueWithin = this.getDurationFromSlug(param.value);
                        break;
                    case ActivityFilterParamKeys.HiddenTypes:
                        const hiddenTypes = this.urlToCulturalActivityTypes(param.value);
                        this.currentOptions.visibleTypes = this.CulturalActivityTypeMetadata.filter((i) => !hiddenTypes.includes(i));
                        break;
                    case ActivityFilterParamKeys.HiddenStatuses:
                        const hiddenStatuses = this.urlToActivityStatuses(param.value);
                        this.currentOptions.visibleStatuses = Object.values(CulturalActivityStatus).filter((i) => !hiddenStatuses.includes(i));
                        break;
                }
            }
        }
    }

    private culturalActivityTypesToUrl(types: ICulturalActivityTypeMetadata[]) {
        return types.map((type) => this.CulturalActivityTypeMetadata.indexOf(type))
            .sort((a, b) => a - b)
            .join(",");
    }

    private urlToCulturalActivityTypes(urlString: string) {
        const typeIndexes = this.CulturalActivityTypeMetadata.map((_, idx) => idx);
        return urlString.split(",")
            .map((type) => parseInt(type, 10))
            .filter((type) => !Number.isNaN(type) && typeIndexes.includes(type))
            .sort((a, b) => a - b)
            .map((idx) => this.CulturalActivityTypeMetadata[idx]);
    }

    private activityStatusesToUrl(statuses: CulturalActivityStatus[]) {
        return statuses
            .sort((a, b) => a.localeCompare(b))
            .join(",");
    }

    private urlToActivityStatuses(urlString: string) {
        return urlString.split(",")
            .filter((type) => Object.values(CulturalActivityStatus).includes(type as CulturalActivityStatus))
            .sort((a, b) => a.localeCompare(b))
            .map((status) => status as CulturalActivityStatus);
    }
}
